import 'maplibre-gl/dist/maplibre-gl.css';
import './style.css';

import { initialState, loadHistory, loadMetric, refreshAll } from './data';
import {
  buildLegend,
  createMap,
  syncNationalMarkers,
  setCurrentDirections,
  syncCameraMarkers,
  syncCurrentMarkers,
  syncMarkers,
  syncTideMarkers,
} from './map';
import { currentCurve, type CurrentCurve } from './sources/currents';
import { streamAt, type StreamAt } from './currentChart';
import { buildMarinePopup } from './marinePopup';
import { buildCameraPopup } from './cameraPopup';
import { cameraById, CAMERAS } from './cameras';
import { probeAll } from './cameraStatus';
import { renderFilterPanel } from './filterPanel';
import { matchesFilters } from './filters';
import { displayedMetrics, type MetricKey, type MetricSpec } from './lib/mapMetric';
import { LNM_DISTRICT_11, marineAlerts, zoneShape } from './sources/marine';
import {
  clearZones, fitToBoxes, fitToShapes, mapObscuredBy, onZoneClick, showZones,
  visibleViewport, type ZoneShape,
} from './map';
import type { ZoneSelection } from './marinePopup';
import { loadedOutlines, zoneOutlines } from './sources/marineShapes';
import { buildTidePopup } from './tidePopup';
import { tideStationById } from './tideStations';
import { buildCurrentPopup } from './currentPopup';
import { CURRENT_STATIONS } from './currentStations';
import {
  nationalTideStations, nationalCurrentStations, nearestFirst,
  withinBounds as withinBoundsCoops, type CoopsPoint,
} from './sources/nationalCoops';
import { renderPanel } from './panel';
import { buildPopup } from './popup';
import { STATIONS, TIDE_STATION } from './stations';
import { tidesToday, type TideEvent } from './sources/tides';
import { gateCurrents, type CurrentSet, type CurrentState } from './sources/currents';
import { currentStateAt } from './lib/tideState';
import { renderGateStrip } from './gate';
import { shouldCatchUp, shouldPoll } from './lib/refreshPolicy';
import { detailOffset } from './lib/detailOffset';
import { NATIONAL_REPORT_MIN, type StationState } from './types';
import { nationalStations, type NationalStation } from './sources/nationalBuoys';
import { latestRowsFor } from './sources/ndbc';
import { applyFresher, planRefresh, shouldReloadBulk } from './lib/liveRefresh';

/** Matches the CO-OPS publication cadence; NDBC is slower and the proxy caches it. */
const REFRESH_MS = 6 * 60 * 1000;

/**
 * How long the bulk national file is left alone between reads.
 *
 * Tied to the edge cache's own lifetime: asking sooner would spend bandwidth on
 * bytes that cannot have changed.
 */
const LATEST_CACHE_MS = 15 * 60 * 1000;

const states = initialState();
let tides: TideEvent[] = [];
let currents: CurrentSet | null = null;
let selectedId: string | null = null;
let selectedTideId: string | null = null;
let selectedCurrentId: string | null = null;
let hoveredCurrentId: string | null = null;
let selectedCameraId: string | null = null;
let showCameras = false;
/** Which sensor filters are on. Empty means show everything. */
const activeFilters = new Set<string>();
/** Both off by default — the map is a wind map first. */
let showTides = false;
/** Subordinate tide stations are the majority, so they are opt-in. */
let showSubordinateTides = false;
let showCurrents = false;
let showAllCurrents = false;

const mapHost = document.querySelector<HTMLElement>('#map')!;
const panelHost = document.querySelector<HTMLElement>('#station-list')!;
const statusHost = document.querySelector<HTMLElement>('#status')!;
const gateHost = document.querySelector<HTMLElement>('#gate')!;
const detailHost = document.querySelector<HTMLElement>('#detail')!;
const filterHost = document.querySelector<HTMLElement>('#station-filter')!;
const timeChipHost = document.querySelector<HTMLElement>('#time-chip')!;

/**
 * NDBC's national set, loaded once after the Bay has painted.
 *
 * Deliberately not part of startup: the whole point of adding the country is
 * that the home view costs nothing extra. Zero of these fall inside the opening
 * viewport anyway, so there is nothing to draw until the reader moves.
 */
let national: NationalStation[] = [];

/** When each national station was last refreshed from the ten-minute feed. */
const nationalRefreshed = new Map<string, number>();
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function paintNational(): void {
  const drawn = syncNationalMarkers(
    map, national, openNationalStation, activeFilters, shownMetrics,
  );
  scheduleLiveRefresh(drawn);
}

/**
 * Brings the visible national markers up to the ten-minute feed.
 *
 * The bulk file these are drawn from is an hourly rollup and lags realtime2 by
 * up to an hour, so the map showed one number and opening the station showed a
 * fresher one. Refreshing every marker would be a fetch each, which is the cost
 * the bulk file exists to avoid — so this runs only when few enough are on
 * screen to be read individually.
 *
 * Debounced, because a pan fires this on every settled frame, and each station
 * is remembered so a jiggle back and forth does not re-ask for the same ids.
 */
function scheduleLiveRefresh(drawn: readonly string[]): void {
  const { ids } = planRefresh(drawn, nationalRefreshed);
  if (ids.length === 0) return;

  if (refreshTimer !== null) clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    // Mark before the request, not after: two overlapping pans would otherwise
    // both see an unrefreshed station and ask for it twice.
    const at = Date.now();
    for (const id of ids) nationalRefreshed.set(id, at);

    void latestRowsFor(ids)
      .then((rows) => {
        // Only repaint on a real change, or this would loop: paint schedules a
        // refresh, and an unconditional repaint would schedule another.
        if (applyFresher(national, rows)) {
          syncNationalMarkers(
            map, national, openNationalStation, activeFilters, shownMetrics,
          );
        }
      })
      .catch(() => {
        // The bulk reading stands, and its age is already shown. Let the station
        // be retried rather than pinning it to a failure.
        for (const id of ids) nationalRefreshed.delete(id);
      });
  }, 400);
}

/**
 * The national CO-OPS stations, loaded when their layer is first switched on.
 *
 * Unlike the buoys these carry no reading — CO-OPS answers one station per
 * request, so a value on every marker would be a request per marker. They are
 * placed from the registry and fetched when one is opened.
 */
let nationalTides: CoopsPoint[] = [];
let nationalCurrents: CoopsPoint[] = [];

/** Most markers of one kind to draw at once. Past this the coast is a smear. */
const COOPS_MAX_MARKERS = 250;

/** The slice of a national list that belongs in the current view. */
function coopsInView(all: readonly CoopsPoint[]): CoopsPoint[] {
  if (all.length === 0) return [];
  const b = map.getBounds();
  const bounds = {
    north: b.getNorth(), south: b.getSouth(), east: b.getEast(), west: b.getWest(),
  };
  return nearestFirst(withinBoundsCoops(all, bounds), bounds, COOPS_MAX_MARKERS);
}

function loadNationalTides(): void {
  if (nationalTides.length > 0) return;
  nationalTideStations()
    .then((rows) => { nationalTides = rows; paintSelection(); })
    .catch(() => { /* The Bay's own stations are unaffected. */ });
}

function loadNationalCurrents(): void {
  if (nationalCurrents.length > 0) return;
  nationalCurrentStations()
    .then((rows) => { nationalCurrents = rows; paintSelection(); })
    .catch(() => { /* The Bay's own stations are unaffected. */ });
}

/** When the bulk national file was last read, for the reload gate. */
let nationalLoadedAt: number | null = null;

/**
 * Re-reads the bulk file on the refresh poll.
 *
 * Folded rather than replaced: a station the per-station refresh already
 * brought up to the ten-minute feed must not be pushed back to the hourly
 * rollup's older figure, so only strictly newer readings win.
 */
function reloadNationalBulk(): void {
  if (!shouldReloadBulk(nationalLoadedAt, Date.now(), LATEST_CACHE_MS)) return;

  nationalStations(true)
    .then((rows) => {
      nationalLoadedAt = Date.now();
      const changed = applyFresher(national, new Map(rows.map((r) => [r.id, r])));

      // Stations that have come on the air since the page loaded.
      const known = new Set(national.map((s) => s.id));
      const added = rows.filter((r) => !known.has(r.id));
      if (added.length > 0) national.push(...added);

      if (changed || added.length > 0) paintNational();
    })
    .catch(() => {
      /* The readings on screen stand, with their ages already shown. */
    });
}

function loadNational(): void {
  nationalStations()
    .then((rows) => {
      national = rows;
      nationalLoadedAt = Date.now();
      paintNational();
    })
    .catch(() => {
      /* Offshore coverage is additive; without it the Bay is unaffected. */
    });
}

/** Recomputed on demand — the phase changes with the clock, not with a fetch. */
function gateState(): CurrentState | null {
  return currents ? currentStateAt(currents) : null;
}

const map = createMap(mapHost);

// A basemap that fails to load is otherwise completely silent — the page just
// sits on an empty canvas with no clue why.
map.on('error', (e) => {
  console.error('[map]', e.error ?? e);
});

if (import.meta.env.DEV) {
  (window as unknown as { __map: typeof map }).__map = map;
}

/** Predictions already downloaded, keyed by station. Streams are derived live. */
const loadedCurves = new Map<string, CurrentCurve>();
/** The moment the open chart is being scrubbed to, if any. */
let scrubbedAt: Date | null = null;

/**
 * Derives every direction badge from state, in one place.
 *
 * Priority runs scrub > hover > show-all, so the most deliberate gesture wins.
 * Anything not named here is cleared, which is what stops a badge outliving the
 * interaction that produced it.
 */
function refreshCurrentBadges(): void {
  const streams = new Map<string, StreamAt>();
  const now = new Date();

  // Scrubbing one station's chart moves the whole map to that moment. Each
  // station is still evaluated against its own prediction, so the Bay is shown
  // as it will actually stand — the turn reaching Carquinez hours after the
  // Gate, rather than every arrow swinging in unison.
  const at = scrubbedAt ?? now;

  if (showAllCurrents) {
    for (const [id, curve] of loadedCurves) {
      const s = streamAt(curve, at);
      if (s) streams.set(id, s);
    }
  }

  for (const id of [hoveredCurrentId, selectedCurrentId]) {
    if (!id) continue;
    const curve = loadedCurves.get(id);
    if (!curve) continue;
    if (id === selectedCurrentId && !scrubbedAt && !showAllCurrents && id !== hoveredCurrentId) {
      continue; // Selected but idle: nothing is asking for a bearing.
    }
    const s = streamAt(curve, at);
    if (s) streams.set(id, s);
  }

  setCurrentDirections(streams);
  renderTimeChip(scrubbedAt);
}

/**
 * Says out loud which moment the map is showing.
 *
 * Without it, scrubbing silently repaints every arrow to a time hours away and
 * the map still looks like live data — the one place this view could actively
 * mislead.
 */
function renderTimeChip(at: Date | null): void {
  if (!at) {
    timeChipHost.hidden = true;
    return;
  }
  timeChipHost.hidden = false;
  timeChipHost.innerHTML =
    `<span class="chip-label">Showing</span> ${chipFmt.format(at)}`;
}

const chipFmt = new Intl.DateTimeFormat([], {
  weekday: 'short', hour: 'numeric', minute: '2-digit',
});

/**
 * Loads a station's prediction if it is not already in hand, then repaints.
 * Cached per station, so exploring the Bay by hovering costs one request each.
 */
function ensureCurve(id: string): void {
  if (loadedCurves.has(id)) {
    refreshCurrentBadges();
    return;
  }
  currentCurve(id)
    .then((curve) => {
      loadedCurves.set(id, curve);
      refreshCurrentBadges();
    })
    .catch(() => {
      /* A missing prediction just means no badge. */
    });
}

/**
 * Repaints every layer against the current selection. The station kinds and the
 * cameras are mutually exclusive, so this is the single place that decides which
 * one is lit.
 */
function paintSelection(): void {
  syncMarkers(map, states, openStation, selectedId, activeFilters, shownMetrics, metricValues);
  renderPanel(panelHost, states, openStation, selectedId, activeFilters);
  const tidesToShow = showSubordinateTides
    ? nationalTides
    : nationalTides.filter((t) => !t.subordinate);
  syncTideMarkers(map, showTides, openTideStation, selectedTideId, coopsInView(tidesToShow));
  syncCameraMarkers(map, showCameras, openCamera, selectedCameraId);
  syncCurrentMarkers(
    map,
    showCurrents,
    {
      onSelect: openCurrentStation,
      onHover: (id, entering) => {
        hoveredCurrentId = entering ? id : null;
        if (entering) ensureCurve(id);
        else refreshCurrentBadges();
      },
    },
    selectedCurrentId,
    coopsInView(nationalCurrents),
  );
  // Reconcile badges against the new selection — this is what keeps one from
  // surviving the interaction that created it.
  refreshCurrentBadges();
  // The national layer answers the same filter, so it repaints with everything
  // else. Without this, selecting Water temperature would relabel the Bay and
  // leave three hundred offshore markers still reading knots.
  paintNational();
}

/** Opens the panel on a station of any kind, nudged clear of the panel itself. */
function showDetail(content: HTMLElement, lon: number, lat: number): void {
  detailHost.hidden = false;
  detailHost.textContent = '';
  detailHost.append(content);
  // Panels that must measure themselves get a deterministic hook here, rather
  // than waiting on a frame a background tab never renders.
  content.dispatchEvent(new CustomEvent('mount'));
  detailHost.focus({ preventScroll: true });

  paintSelection();

  // With a bottom sheet the map is sticky at the top of a scrolling page, so
  // bring it into view before framing anything.
  const sheet = detailHost.offsetWidth >= mapHost.offsetWidth - 1;
  if (sheet) window.scrollTo({ top: 0, behavior: 'smooth' });

  map.easeTo({
    center: [lon, lat],
    offset: detailOffset({
      panelWidth: detailHost.offsetWidth,
      panelHeight: detailHost.offsetHeight,
      mapWidth: mapHost.offsetWidth,
      mapHeight: mapHost.offsetHeight,
      viewportHeight: window.innerHeight,
    }),
    duration: 400,
  });
}

/**
 * Station detail lives in a side panel rather than a map popup.
 *
 * A buoy stacks four time-aligned panels; anchored to a pin that is taller than
 * the space beside it at any zoom, so it always ran off the viewport. A panel
 * gets full height, needs no pan gymnastics, and drops to a sheet on mobile.
 */
function openStation(id: string): void {
  const state = states.get(id);
  if (!state) return;

  selectedId = id;
  selectedTideId = null;
  selectedCurrentId = null;
  selectedCameraId = null;
  showDetail(buildPopup(state, closeDetail), state.station.lon, state.station.lat);
}

/**
 * The marine forecast is Bay-wide rather than per-station, so it opens from the
 * sidebar instead of a marker.
 */
/**
 * Redraws the marine panel's region strip, while that panel is open.
 *
 * Null when it is closed, which is also what stops the map's move handler from
 * poking at a panel that is no longer on screen.
 */
let redrawMarineRegions: (() => void) | null = null;

/** Jumps the open marine panel to the card speaking for a zone. */
let focusMarineZone: ((zoneId: string) => void) | null = null;

function openMarineForecast(): void {
  selectedId = null;
  selectedTideId = null;
  selectedCurrentId = null;
  scrubbedAt = null;

  /**
   * Guards against an earlier selection landing after a later one.
   *
   * Zone geometry is fetched, so two quick clicks race: switching a card off
   * clears the map at once, then the previous click's fetch resolves and paints
   * the zone straight back. Only the newest request may touch the map.
   */
  let zonePaint = 0;

  /** The zone cards actually chosen, so a hover preview can be undone. */
  let liveSelections: ZoneSelection[] = [];

  const paintSelections = (selections: ZoneSelection[], grew: boolean): void => {
    const token = ++zonePaint;

    // Clearing the last card must not wait on a fetch, or the map keeps showing
    // water the panel says is no longer selected.
    if (selections.length === 0) {
      clearZones(map);
      return;
    }

    // Every selected card's zones, resolved together so the map is repainted
    // once from the whole selection rather than accumulating layers per click.
    const pending = selections.flatMap((sel) =>
      sel.zones.map((z) => zoneShape(z).then((geometry) => ({ geometry, tone: sel.tone, id: z }))),
    );

    Promise.all(pending)
      .then((results) => {
        if (token !== zonePaint) return;
        const found: ZoneShape[] = results.flatMap((r) =>
          r.geometry === null ? [] : [{ geometry: r.geometry, tone: r.tone, id: r.id }],
        );
        showZones(map, found);
        // Framing only on growth: refitting when a card is switched off would
        // yank the camera as a side effect of tidying up.
        if (grew && found.length) {
          fitToShapes(map, found.map((f) => f.geometry), mapObscuredBy(map, detailHost));
        }
      })
      .catch(() => {
        /* Without geometry the forecast text still stands on its own. */
      });
  };

  const content = buildMarinePopup(
    closeDetail,
    (selections, grew) => {
      liveSelections = selections;
      paintSelections(selections, grew);
    },
    {
      // Only the water the panel is not sitting on top of.
      getView: () => visibleViewport(map, mapObscuredBy(map, detailHost)),
      /*
       * Outlines a region's waters under the pointer.
       *
       * Drawn from the shipped bounding boxes rather than real geometry: a
       * region runs to 36 zones at ~60 KB each, which is not a hover's to
       * spend. Boxes say where the forecast reaches, which is the question a
       * preview answers.
       */
      /*
       * Frames a region when it is chosen.
       *
       * Built from the same boxes as the preview, so the map goes exactly where
       * the outline said it would.
       */
      onReady: (redrawRegions) => {
        redrawMarineRegions = redrawRegions;
      },
      onZoneFocus: (focus) => {
        focusMarineZone = focus;
      },
      onRegionFocus: (boxes) => {
        if (boxes.length === 0) return;
        fitToBoxes(map, boxes, mapObscuredBy(map, detailHost));
      },
      onRegionPreview: (zoneIds) => {
        const token = ++zonePaint;
        if (!zoneIds) {
          // Put back whatever the reader had actually chosen.
          if (liveSelections.length === 0) clearZones(map);
          else paintSelections(liveSelections, false);
          return;
        }

        // An empty list is the panel asking us to warm the chunk, nothing more.
        if (zoneIds.length === 0) {
          void zoneOutlines().catch(() => { /* preview is optional */ });
          return;
        }

        const draw = (outlines: Record<string, GeoJSON.Geometry>): void => {
          if (token !== zonePaint) return;   // the pointer has moved on
          showZones(
            map,
            zoneIds.flatMap((id) => {
              const geometry = outlines[id];
              return geometry ? [{ geometry, tone: 'plain' as const, id }] : [];
            }),
          );
        };

        // Already in hand after the first hover, so this paints without a wait.
        const have = loadedOutlines();
        if (have) draw(have);
        else void zoneOutlines().then(draw).catch(() => { /* preview is optional */ });
      },
    },
  );

  detailHost.hidden = false;
  detailHost.textContent = '';
  detailHost.append(content);
  // Same hook the other panels get. The region strip asks how much of the map
  // this panel is covering, and until it is attached the answer is zero — which
  // offered regions hidden behind it.
  content.dispatchEvent(new CustomEvent('mount'));
  detailHost.focus({ preventScroll: true });
  paintSelection();
}

/** A live look at the water, to put beside the numbers. */
function openCamera(id: string): void {
  const camera = cameraById(id);
  if (!camera) return;

  selectedId = null;
  selectedTideId = null;
  selectedCurrentId = null;
  selectedCameraId = id;
  scrubbedAt = null;
  showDetail(buildCameraPopup(camera, closeDetail), camera.lon, camera.lat);
}

/**
 * State for national stations, built the first time one is opened.
 *
 * Kept apart from `states` so the refresh loop and the marker layer keep working
 * on the curated eighteen alone, which is what makes the Bay unaffected by any
 * of this.
 */
const nationalStates = new Map<string, StationState>();

/**
 * A national station opened as a full station, not a summary.
 *
 * The map draws these from NDBC's hourly bulk file, but realtime2 carries
 * ten-minute readings going back weeks for the same ids — so opening one
 * upgrades it to the same panel a Bay buoy gets, charts and all, rather than
 * settling for the hourly figure the marker was drawn from.
 *
 * Sensors are declared optimistically: `panelsFor` drops any series that comes
 * back empty, so claiming all three costs nothing and avoids missing a sensor
 * whose value merely happened to be absent from the one bulk row we saw.
 */
function openNationalStation(id: string): void {
  const s = national.find((x) => x.id === id);
  if (!s) return;

  let state = nationalStates.get(id);
  if (!state) {
    state = {
      station: {
        id: s.id,
        name: s.name,
        lat: s.lat,
        lon: s.lon,
        source: 'ndbc',
        kind: s.type === 'buoy' ? 'buoy' : 'shore',
        sensors: { waves: true, waterTemp: true, airTemp: true },
        // realtime2 cadence, not the hourly bulk file the marker came from.
        // Shared with the marker so both judge staleness by the same window.
        reportMinutes: NATIONAL_REPORT_MIN,
      },
      // A wave buoy reports no wind, so it opens with no reading and fills in
      // from realtime2 below if that carries one.
      latest: s.speedKt === null
        ? null
        : { t: s.t, speedKt: s.speedKt, dirDeg: s.dirDeg, gustKt: s.gustKt },
      history: null,
      error: null,
      loading: false,
    };
    nationalStates.set(id, state);
  }

  selectedId = null;
  selectedTideId = null;
  selectedCurrentId = null;
  selectedCameraId = null;
  scrubbedAt = null;
  showDetail(buildPopup(state, closeDetail), s.lon, s.lat);

  // The marker was drawn from the hourly bulk file; the panel upgrades itself to
  // realtime2 on open. Without folding that back, the map and the panel show
  // different numbers for the same buoy at the same moment. loadHistory caches
  // on the state, so this shares the popup's fetch rather than making a second.
  void loadHistory(state)
    .then(() => {
      const fresh = state.latest;
      if (!fresh || fresh.t <= s.t) return;
      s.t = fresh.t;
      s.speedKt = fresh.speedKt;
      s.dirDeg = fresh.dirDeg;
      s.gustKt = fresh.gustKt;
      paintNational();
    })
    .catch(() => {
      /* The panel reports its own failure; the marker keeps the bulk reading. */
    });
}

/** Tide stations carry no wind, so they get their own detail view. */
function openTideStation(id: string): void {
  // Curated first, then the national registry — the panel is identical either
  // way, since a tide station has nothing to show until its day is fetched.
  const station = tideStationById(id) ?? nationalTides.find((t) => t.id === id);
  if (!station) return;

  selectedId = null;
  selectedTideId = id;
  selectedCurrentId = null;
  selectedCameraId = null;
  showDetail(buildTidePopup(station, closeDetail), station.lon, station.lat);
}

/** Current stations get a dial that follows the cursor across the prediction. */
function openCurrentStation(id: string): void {
  const station = CURRENT_STATIONS.find((c) => c.id === id)
    ?? nationalCurrents.find((c) => c.id === id);
  if (!station) return;

  selectedId = null;
  selectedTideId = null;
  selectedCurrentId = id;
  selectedCameraId = null;

  // Scrubbing the chart drives the badge on this station's marker, so the map
  // and the dial always show the same moment.
  scrubbedAt = null;
  const content = buildCurrentPopup(station, closeDetail, (at) => {
    scrubbedAt = at;
    refreshCurrentBadges();
  });

  ensureCurve(id);
  showDetail(content, station.lon, station.lat);
}

function closeDetail(): void {
  detailHost.hidden = true;
  detailHost.textContent = '';
  // The marine panel is gone, so nothing should still be reaching into it.
  redrawMarineRegions = null;
  focusMarineZone = null;
  selectedId = null;
  selectedTideId = null;
  selectedCurrentId = null;
  selectedCameraId = null;
  scrubbedAt = null;
  clearZones(map);
  paintSelection();
}

function setStatus(text: string, busy = false): void {
  statusHost.textContent = text;
  // Hold the previous render at reduced opacity rather than flashing a skeleton.
  document.body.classList.toggle('is-refreshing', busy);
}

function paint(): void {
  paintSelection();
  renderGateStrip(gateHost, tides, gateState());
}

/** The measures the map labels currently carry, and their values per station. */
let shownMetrics: MetricSpec[] = [];
let metricValues = new Map<MetricKey, Map<string, number>>();
/** Guards against a slower metric fetch landing after the filter moved on. */
let metricLoad = 0;

/**
 * Loads the values behind every selected metric.
 *
 * Runs on each filter change and each refresh, and fetches nothing unless a
 * sensor filter is active. Each metric resolves independently so a slow one
 * cannot hold up the labels for the others.
 */
async function syncMetric(): Promise<void> {
  const next = displayedMetrics(activeFilters);
  const token = ++metricLoad;

  if (next.length === 0) {
    shownMetrics = [];
    metricValues = new Map();
    paintSelection();
    return;
  }

  // Show the metrics immediately with whatever is already known; the labels fill
  // in as values arrive rather than the map sitting on wind until they do.
  shownMetrics = next;
  paintSelection();

  const wanted = STATIONS.filter((s) => matchesFilters(s, activeFilters));
  // Wind is already on every station's state; only the sensors need fetching.
  const loaded = await Promise.all(
    next
      .filter((m) => m.key !== 'wind')
      .map(async (m) => [m.key, await loadMetric(m.key, wanted)] as const),
  );
  if (token !== metricLoad) return;

  metricValues = new Map(loaded);
  paintSelection();
}

function applyFilters(): void {
  // A filtered-out station must not stay open behind the map.
  if (selectedId && !matchesFilters(states.get(selectedId)!.station, activeFilters)) {
    closeDetail();
  }
  renderFilterPanel(filterHost, activeFilters, applyFilters);
  paintSelection();
  void syncMetric();
}

/** When the readings were last fetched, so a returning tab knows if it is stale. */
let lastRefreshAt = 0;

async function refresh(): Promise<void> {
  setStatus('Updating…', true);

  await refreshAll(states, paint);
  lastRefreshAt = Date.now();
  // The national layer has its own cadence: the markers in view are refreshed
  // by paintNational above, and this catches everything that refresh cannot —
  // off-screen stations, and any view too crowded to refresh per station.
  reloadNationalBulk();
  // The buoy batch just refreshed, so any metric drawn from it has moved too.
  void syncMetric();

  const failed = [...states.values()].filter((s) => !s.latest).length;
  const stamp = new Intl.DateTimeFormat([], { hour: 'numeric', minute: '2-digit' }).format(new Date());
  setStatus(
    failed === 0
      ? `Updated ${stamp}`
      : `Updated ${stamp} · ${failed} of ${STATIONS.length} unavailable`,
  );
}

/*
 * Nothing here waits on the map.
 *
 * Markers, the legend, and the station list are all plain DOM, and the CO-OPS
 * feed is independent of the basemap. Gating startup on `map.on('load')` meant a
 * basemap that never finished — a blocked CDN, a WebGL failure, or simply a
 * backgrounded tab where requestAnimationFrame never fires — left the page stuck
 * on "Starting…" with no readings at all, when it could have shown every number.
 */
mapHost.append(buildLegend());
renderFilterPanel(filterHost, activeFilters, applyFilters);
paint();

const tideSubToggle = document.querySelector<HTMLInputElement>('#tide-sub-toggle')!;

tideSubToggle.addEventListener('change', (e) => {
  showSubordinateTides = (e.target as HTMLInputElement).checked;
  // A subordinate station left open must not survive being hidden.
  if (!showSubordinateTides && selectedTideId
      && nationalTides.find((t) => t.id === selectedTideId)?.subordinate) {
    closeDetail();
  } else {
    paintSelection();
  }
});

document.querySelector<HTMLInputElement>('#tide-toggle')!
  .addEventListener('change', (e) => {
    showTides = (e.target as HTMLInputElement).checked;
    // The national registry is its own chunk, fetched the first time the layer
    // is asked for — a reader who never opens it never pays for it.
    if (showTides) loadNationalTides();

    // The sub-option is meaningless without the layer, so it follows it down.
    tideSubToggle.disabled = !showTides;
    if (!showTides) {
      tideSubToggle.checked = false;
      showSubordinateTides = false;
    }

    // Hiding a layer must not leave its panel orphaned behind the map.
    if (!showTides && selectedTideId) closeDetail();
    else paintSelection();
  });

const allCurrentsToggle = document.querySelector<HTMLInputElement>('#current-all-toggle')!;

document.querySelector<HTMLInputElement>('#current-toggle')!
  .addEventListener('change', (e) => {
    showCurrents = (e.target as HTMLInputElement).checked;
    if (showCurrents) loadNationalCurrents();

    // "Show all" is meaningless without the layer, so it follows it down.
    allCurrentsToggle.disabled = !showCurrents;
    if (!showCurrents) {
      allCurrentsToggle.checked = false;
      showAllCurrents = false;
    }

    if (!showCurrents && selectedCurrentId) closeDetail();
    else paintSelection();
  });

allCurrentsToggle.addEventListener('change', (e) => {
  showAllCurrents = (e.target as HTMLInputElement).checked;
  if (showAllCurrents) loadAllCurves();
  refreshCurrentBadges();
});

/**
 * Fetches every current station for the show-all view.
 *
 * Fired together rather than paced: each request already retries once after a
 * pause, which is what absorbs the 403 CO-OPS returns to a burst. Badges appear
 * as each station lands instead of waiting for the slowest.
 */
function loadAllCurves(): void {
  for (const station of CURRENT_STATIONS) ensureCurve(station.id);
}

document.querySelector<HTMLInputElement>('#camera-toggle')!
  .addEventListener('change', (e) => {
    showCameras = (e.target as HTMLInputElement).checked;
    // Caltrans answers an outage with a "Temporarily Unavailable" graphic and
    // HTTP 200, so the only way to know is to look at the pixels.
    if (showCameras) probeAll(CAMERAS, paintSelection);
    // Hiding the layer must not leave its panel orphaned behind the map.
    if (!showCameras && selectedCameraId) closeDetail();
    else paintSelection();
  });

document.querySelector<HTMLButtonElement>('#marine-button')!
  .addEventListener('click', openMarineForecast);

document.querySelector<HTMLAnchorElement>('#lnm-link')!.href = LNM_DISTRICT_11;

// An active gale warning is worth seeing without opening anything.
marineAlerts()
  .then((alerts) => {
    const badge = document.querySelector<HTMLElement>('#marine-alert-count')!;
    if (alerts.length === 0) return;
    badge.hidden = false;
    badge.textContent = String(alerts.length);
    badge.title = alerts.map((a) => a.event).join(', ');
    document.querySelector('#marine-button')!.classList.add('has-alerts');
  })
  .catch(() => {
    /* The button still opens the forecast. */
  });

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && !detailHost.hidden) closeDetail();
});

// moveend, not move: this adds and removes DOM markers, and rebuilding them on
// every frame of a pan would stutter. Zoom is included because the layer has a
// minimum zoom and crossing it changes everything on screen.
/** Both national layers are view-dependent, so they repaint together. */
function paintOnViewChange(): void {
  paintNational();
  // The marine panel's region tabs offer the regions on screen, so they follow
  // the map rather than the moment the panel happened to open.
  redrawMarineRegions?.();
  // Only worth a repaint when a national CO-OPS layer is actually showing.
  if ((showTides && nationalTides.length > 0) || (showCurrents && nationalCurrents.length > 0)) {
    paintSelection();
  }
}

/*
 * Clicking water on the map opens what the panel says about it.
 *
 * The panel could already light up a zone for a card; without this there was no
 * way back — you could go from the text to the water but not from the water to
 * the text.
 */
onZoneClick(map, (zoneId) => {
  focusMarineZone?.(zoneId);
});

map.on('moveend', paintOnViewChange);
map.on('zoomend', paintOnViewChange);

// Tide and current are context, not the headline. Either failing must leave the
// wind readings untouched, so they settle independently and repaint when ready.
void Promise.allSettled([
  tidesToday(TIDE_STATION).then((t) => { tides = t; }),
  gateCurrents().then((c) => { currents = c; }),
]).then(() => renderGateStrip(gateHost, tides, gateState()));

await refresh();

// Only now: the Bay is on screen and the country is additive. Zero national
// stations fall inside the opening viewport, so this draws nothing until the
// reader moves — it is loaded early so that the move is instant when they do.
loadNational();

// Never poll a tab nobody is looking at.
setInterval(() => {
  if (shouldPoll(document.hidden)) void refresh();
}, REFRESH_MS);

document.addEventListener('visibilitychange', () => {
  if (document.hidden) return;
  // Back in view: catch up at once if the readings went stale while the tab was
  // in the background, rather than showing old data until the next tick.
  if (shouldCatchUp(lastRefreshAt, Date.now(), REFRESH_MS)) void refresh();
});
